import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  User, 
  Mail, 
  MapPin, 
  Settings, 
  CreditCard, 
  ChevronDown,
  ChevronRight,
  Check,
  AlertTriangle,
  Loader2,
  SquareUserRound,
  ShieldAlert,
  PictureInPicture2,
  History,
  Clock
} from 'lucide-react';
import { supabase, getApiBaseUrl } from '../../services/api';
import { getCrimeTypes, getCrimeDisplayName } from '../../constants/crimes';
import {
  locationTypes,
  locationNames,
  getStateAbbreviation,
  formatLocationNameWithStateAbbrev,
} from '../../constants/locations';
import './Profile.css';
import '../Modals/Modals.css';
import { isNewUtcMonth, getDetectedTimeZone } from '../../utils/planTier';

const PLAN_LIMITS = {
  free:       { searches: 5,    alerts: 0,    exports: 0,    prints: 10,   outputs: null, label: 'Free' },
  lieutenant: { searches: 50,   alerts: 10,   exports: 0,    prints: 100,  outputs: null, label: 'Lieutenant' },
  captain:    { searches: 200,  alerts: 20,   exports: null, prints: null, outputs: 400,  label: 'Captain' },
  chief:      { searches: null, alerts: null, exports: null, prints: null, outputs: 1000, label: 'Chief' },
};

const hasCombinedOutputs = (plan) => plan === 'captain' || plan === 'chief';

const planSlugToHeaderLabel = (slug) => {
  const p = String(slug || 'free').toLowerCase();
  const row = PLAN_LIMITS[p];
  return (row?.label || 'Free').toUpperCase();
};

function formatHistoryMonthLabel(yyyyMm) {
  const parts = String(yyyyMm).split('-');
  if (parts.length !== 2) return yyyyMm;
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return yyyyMm;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  });
}

const ProfileModal = ({ isOpen, onClose, onDefaultViewChange, initialSection, liveSearchesUsed, liveExportsUsed, livePrintsUsed, liveSearchHistory, onDefaultLocationChange }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(initialSection || 'account');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isCrimeDropdownOpen, setIsCrimeDropdownOpen] = useState(false);
  const [isViewDropdownOpen, setIsViewDropdownOpen] = useState(false);
  const [isHistoryMonthDropdownOpen, setIsHistoryMonthDropdownOpen] = useState(false);
  const [lastFetched, setLastFetched] = useState(null);
  const [historyMonthKey, setHistoryMonthKey] = useState('current');
  const [historyLocationFilter, setHistoryLocationFilter] = useState('');
  const [collapsedPlanSegments, setCollapsedPlanSegments] = useState(new Set());
  
  const [fullName, setFullName] = useState('');
  const [defaultLocationName, setDefaultLocationName] = useState('');
  const [defaultLocationType, setDefaultLocationType] = useState('');
  const [defaultCrime, setDefaultCrime] = useState('all_crime');
  const [defaultView, setDefaultView] = useState('table');
  const [originalValues, setOriginalValues] = useState({});

  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [locationSearchResults, setLocationSearchResults] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [locationHighlightedIndex, setLocationHighlightedIndex] = useState(-1);
  const [isLocationKeyboardNav, setIsLocationKeyboardNav] = useState(false);
  const sessionUserIdRef = useRef(null);

  const crimeButtonRef = useRef(null);
  const viewButtonRef = useRef(null);
  const historyMonthButtonRef = useRef(null);
  const locationSearchRef = useRef(null);
  const locationInputRef = useRef(null);
  const locationDropdownRef = useRef(null);

  const hasChanges = useMemo(() => {
    if (activeSection === 'account') {
      return fullName !== (originalValues.fullName || '');
    }
    if (activeSection === 'settings') {
      return (
        defaultLocationName !== (originalValues.defaultLocationName || '') ||
        defaultCrime !== (originalValues.defaultCrime || 'all_crime') ||
        defaultView !== (originalValues.defaultView || 'table')
      );
    }
    return false;
  }, [fullName, defaultLocationName, defaultCrime, defaultView, originalValues, activeSection]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInDropdown = event.target.closest('.profile-custom-dropdown');
      const isInPortal = event.target.closest('.profile-dropdown-menu');
      const isInLocationSearch = event.target.closest('.profile-location-search-wrapper');
      const isInLocationDropdown = event.target.closest('.profile-location-dropdown');
      
      if (!isInDropdown && !isInPortal) {
        setIsCrimeDropdownOpen(false);
        setIsViewDropdownOpen(false);
        setIsHistoryMonthDropdownOpen(false);
      }

      if (!isInLocationSearch && !isInLocationDropdown) {
        setShowLocationDropdown(false);
        setLocationSearchResults([]);
        setLocationHighlightedIndex(-1);
        if (!defaultLocationName) {
          setDefaultLocationName(originalValues.defaultLocationName || '');
          setDefaultLocationType(originalValues.defaultLocationType || '');
        }
        setLocationSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [defaultLocationName, originalValues]);

  useEffect(() => {
    if (isOpen) {
      fetchUserData();
      document.body.style.overflow = 'hidden';
      if (initialSection) {
        setActiveSection(initialSection);
      }
      setHistoryMonthKey('current');
      setIsHistoryMonthDropdownOpen(false);
      setHistoryLocationFilter('');
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [isOpen, initialSection]); 

  useEffect(() => {
    if (!profile || historyMonthKey === 'current') return;
    const archive = profile.search_history_archive;
    const map =
      archive && typeof archive === 'object' && !Array.isArray(archive) ? archive : null;
    if (!map || !Array.isArray(map[historyMonthKey])) {
      setHistoryMonthKey('current');
    }
  }, [profile, historyMonthKey]);

  const fetchUserData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setLoading(false);
        return;
      }

      const uid = session.user?.id;
      const uidChanged = uid != null && uid !== sessionUserIdRef.current;
      if (uidChanged) {
        sessionUserIdRef.current = uid;
        setProfile(null);
        setLastFetched(null);
      }

      setUser(session.user);

      const shouldFetch =
        uidChanged || !lastFetched || Date.now() - lastFetched > 30000;
      if (!shouldFetch && profile) {
        setLoading(false);
        return;
      }

      if (uidChanged || !profile) {
        setLoading(true);
      }

      const response = await fetch(`${getApiBaseUrl()}/user/profile`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      
      if (response.ok) {
        const profileData = await response.json();
        setProfile(profileData);
        
        const values = {
          fullName: profileData.full_name || '',
          defaultLocationName: profileData.default_location_name || '',
          defaultLocationType: profileData.default_location_type || '',
          defaultCrime: profileData.default_crime || 'all_crime',
          defaultView: profileData.default_view || 'table'
        };
        
        setFullName(values.fullName);
        setDefaultLocationName(values.defaultLocationName);
        setDefaultLocationType(values.defaultLocationType);
        setDefaultCrime(values.defaultCrime);
        setDefaultView(values.defaultView);
        setOriginalValues(values);
        setLastFetched(Date.now());
      } else {
        const errBody = await response.text().catch(() => '');
        console.error(
          'Profile fetch failed',
          response.status,
          errBody.slice(0, 300)
        );
        setMessage({ type: 'error', text: 'Failed to load profile' });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setMessage({ type: 'error', text: 'Failed to load profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFullName(originalValues.fullName || '');
    setDefaultLocationName(originalValues.defaultLocationName || '');
    setDefaultLocationType(originalValues.defaultLocationType || '');
    setDefaultCrime(originalValues.defaultCrime || 'all_crime');
    setDefaultView(originalValues.defaultView || 'table');
    setMessage({ type: '', text: '' });
    setIsCrimeDropdownOpen(false);
    setIsViewDropdownOpen(false);
    setIsHistoryMonthDropdownOpen(false);
    setHistoryLocationFilter('');
    setLocationSearchQuery('');
    setLocationSearchResults([]);
    setShowLocationDropdown(false);
    setLocationHighlightedIndex(-1);
    setActiveSection(initialSection || 'account');
    onClose();
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    const locationChanged = activeSection === 'settings' &&
      (defaultLocationName !== (originalValues.defaultLocationName || '') ||
       defaultLocationType !== (originalValues.defaultLocationType || ''));

    if (locationChanged && onDefaultLocationChange) {
      const result = await onDefaultLocationChange(defaultLocationName, defaultLocationType);
      if (!result?.allowed) {
        setDefaultLocationName(originalValues.defaultLocationName || '');
        setDefaultLocationType(originalValues.defaultLocationType || '');
        const isFree = (profile?.subscription_plan || 'free') === 'free';
        setMessage({
          type: 'error',
          text: isFree
            ? 'Search limit reached. Upgrade to change your default location.'
            : 'Monthly search limit reached. Upgrade to change your default location.'
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 4000);
        setSaving(false);
        return;
      }
    }
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${getApiBaseUrl()}/user/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          full_name: fullName,
          default_location_name: defaultLocationName,
          default_location_type: defaultLocationType,
          default_crime: defaultCrime,
          default_view: defaultView
        })
      });

      if (response.ok) {
        const updatedProfile = await response.json();
        setProfile(updatedProfile);
        setOriginalValues({
          fullName,
          defaultLocationName,
          defaultLocationType,
          defaultCrime,
          defaultView
        });
        if (activeSection === 'settings') {
          onDefaultViewChange?.(defaultView);
        }
        setMessage({ type: 'success', text: 'Profile saved successfully' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setMessage({ type: 'error', text: 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    onClose();
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${getApiBaseUrl()}/user/account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
  
      if (!response.ok) {
        throw new Error('Failed to delete');
      }
  
      await supabase.auth.signOut();
      onClose();
    } catch (error) {
      console.error('Error deleting account:', error);
      setMessage({ type: 'error', text: 'Failed to delete account. Please try again.' });
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const viewTypes = [
    { value: 'table', label: 'Data' },
    { value: 'chart', label: 'Charts' }
  ];

  const getSelectedCrimeLabel = () => {
    if (defaultCrime === 'all_crime') return 'All Crime';
    return getCrimeDisplayName(defaultCrime, 'FBI_CIUS');
  };

  const getSelectedViewLabel = () => {
    const view = viewTypes.find(v => v.value === defaultView);
    return view ? view.label : 'Data';
  };

  const formatLocationDisplay = (name, type) => {
    if (!name) return '';
    if ((type === 'county' || type === 'city') && name.includes(', ')) {
      const parts = name.split(', ');
      const local = parts.slice(0, -1).join(', ');
      const state = parts[parts.length - 1];
      const stateAbbr = getStateAbbreviation(state.trim());
      return `${local}, ${stateAbbr}`;
    }
    return name;
  };

  const normalizeName = (name) => {
    return name.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/-/g, ' ')
      .replace(/[.(),\-']/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const scrollToLocationHighlighted = (index) => {
    if (locationDropdownRef.current) {
      const element = locationDropdownRef.current.querySelector(`[data-index="${index}"]`);
      if (element) {
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  };

  const processLocationResults = (locations, type, query) => {
    const normalizedQuery = normalizeName(query);
    const trimmedQuery = query.trim();

    if (normalizedQuery.length <= 2) {
      const matches = locations
        .filter(name => normalizeName(name).includes(normalizedQuery))
        .map(name => ({ name, type, isExactMatch: normalizeName(name).startsWith(normalizedQuery) }))
        .sort((a, b) => {
          if (a.isExactMatch && !b.isExactMatch) return -1;
          if (!a.isExactMatch && b.isExactMatch) return 1;
          return 0;
        });
      return matches.slice(0, 5);
    }

    if ((type === 'county' || type === 'city') && normalizedQuery.length > 0) {
      const startsWithMatches = [];
      const containsMatches = [];
      const hasSpace = trimmedQuery.includes(' ');

      locations.forEach(fullName => {
        if (fullName.includes(', ')) {
          const [localName, stateName] = fullName.split(', ');
          const normalizedLocalName = normalizeName(localName);
          const normalizedStateName = normalizeName(stateName);
          const stateAbbr = getStateAbbreviation(stateName);
          const normalizedStateAbbr = normalizeName(stateAbbr);

          let matches = false;
          let startsWithCity = false;
          let exactCityMatch = false;

          if (hasSpace) {
            const lastSpaceIndex = trimmedQuery.lastIndexOf(' ');
            const cityPart = trimmedQuery.substring(0, lastSpaceIndex).trim();
            const statePart = trimmedQuery.substring(lastSpaceIndex + 1).trim();
            const normalizedCityPart = normalizeName(cityPart);
            const normalizedStatePart = normalizeName(statePart);

            const words = trimmedQuery.split(/\s+/);
            let altCityPart = '', altStatePart = '';
            if (words.length >= 3) {
              altCityPart = words.slice(0, -2).join(' ');
              altStatePart = words.slice(-2).join(' ');
            }
            const normalizedAltCityPart = normalizeName(altCityPart);
            const normalizedAltStatePart = normalizeName(altStatePart);

            const exactMatch = normalizedLocalName.includes(normalizeName(trimmedQuery));
            const cityAndStateMatch1 = statePart && normalizedLocalName.includes(normalizedCityPart) &&
              (normalizedStateName.startsWith(normalizedStatePart) || normalizedStateAbbr.startsWith(normalizedStatePart));
            const cityAndStateMatch2 = altCityPart && normalizedLocalName.includes(normalizedAltCityPart) &&
              (normalizedStateName.startsWith(normalizedAltStatePart) || normalizedStateAbbr.startsWith(normalizedAltStatePart));

            if (exactMatch || cityAndStateMatch1 || cityAndStateMatch2) {
              matches = true;
              startsWithCity = normalizedLocalName.startsWith(normalizeName(trimmedQuery)) ||
                normalizedLocalName.startsWith(normalizedCityPart) ||
                (altCityPart && normalizedLocalName.startsWith(normalizedAltCityPart));
              exactCityMatch = normalizedLocalName === normalizeName(trimmedQuery) ||
                normalizedLocalName === normalizedCityPart ||
                (altCityPart && normalizedLocalName === normalizedAltCityPart);
            }
          } else {
            if (normalizedLocalName.includes(normalizedQuery) ||
              normalizedStateName.includes(normalizedQuery) ||
              normalizedStateAbbr.includes(normalizedQuery)) {
              matches = true;
              startsWithCity = normalizedLocalName.startsWith(normalizedQuery);
              exactCityMatch = normalizedLocalName.startsWith(normalizedQuery);
            }
          }

          if (matches) {
            const matchData = { name: fullName, localName, normalizedLocalName, type, isExactMatch: exactCityMatch, matchType: startsWithCity ? 'starts_with' : 'contains' };
            if (startsWithCity) startsWithMatches.push(matchData);
            else containsMatches.push(matchData);
          }
        }
      });

      const allMatches = [...startsWithMatches, ...containsMatches].sort((a, b) => {
        if (a.isExactMatch && !b.isExactMatch) return -1;
        if (!a.isExactMatch && b.isExactMatch) return 1;
        if (a.matchType === 'starts_with' && b.matchType === 'contains') return -1;
        if (a.matchType === 'contains' && b.matchType === 'starts_with') return 1;
        return a.localName.localeCompare(b.localName);
      });

      const exactMatches = allMatches.filter(m => m.isExactMatch);
      if (exactMatches.length >= 5) return exactMatches;
      return allMatches.slice(0, 5);
    }

    const startMatches = locations
      .filter(name => normalizeName(name).startsWith(normalizedQuery))
      .map(name => ({ name, type, isExactMatch: true, matchType: 'starts_with' }));

    const containsMatches = locations
      .filter(name => {
        const n = normalizeName(name);
        return n.includes(normalizedQuery) && !n.startsWith(normalizedQuery);
      })
      .map(name => ({ name, type, isExactMatch: false, matchType: 'contains' }));

    const allMatches = [...startMatches, ...containsMatches].sort((a, b) => {
      if (a.isExactMatch && !b.isExactMatch) return -1;
      if (!a.isExactMatch && b.isExactMatch) return 1;
      if (a.matchType === 'starts_with' && b.matchType === 'contains') return -1;
      if (a.matchType === 'contains' && b.matchType === 'starts_with') return 1;
      return 0;
    });

    const exactMatches = allMatches.filter(m => m.isExactMatch);
    if (exactMatches.length >= 5) return exactMatches;
    return allMatches.slice(0, 5);
  };

  const getFlattenedLocationResults = useCallback(() => {
    const results = [];
    [...locationTypes].reverse().forEach(locationType => {
      const typeResults = locationSearchResults.filter(result => result.type === locationType.value);
      results.push(...typeResults);
    });
    return results;
  }, [locationSearchResults]);

  const handleLocationSearch = (e) => {
    const query = e.target.value;
    setLocationSearchQuery(query);
    setDefaultLocationName('');
    setDefaultLocationType('');
    setLocationHighlightedIndex(-1);
    setIsLocationKeyboardNav(false);

    if (query.length > 0) {
      const lq = query.toLowerCase();
      const metroLetters = ['m', 'me', 'met', 'metr', 'metro'];
      const words = lq.split(/\s+/);
      const hasMetroWord = words.some(word => metroLetters.includes(word));
      const queryWithoutMetro = hasMetroWord ? lq.replace(/\b(m|me|met|metr|metro)\b/g, '').trim() : lq;

      const results = [
        ...processLocationResults(locationNames.City, 'city', lq),
        ...processLocationResults(locationNames.County, 'county', lq),
        ...processLocationResults(locationNames.Metro, 'metro', hasMetroWord ? queryWithoutMetro : lq),
        ...processLocationResults(locationNames.State, 'state', lq),
        ...processLocationResults(locationNames.Division, 'division', lq),
        ...processLocationResults(locationNames.Region, 'region', lq),
        ...processLocationResults(locationNames.Nation, 'nation', lq),
      ];

      setLocationSearchResults(results);
      setShowLocationDropdown(results.length > 0);
    } else {
      setLocationSearchResults([]);
      setShowLocationDropdown(false);
    }
  };

  const handleLocationResultSelect = (result) => {
    setDefaultLocationName(result.name);
    setDefaultLocationType(result.type);
    setLocationSearchQuery('');
    setLocationSearchResults([]);
    setShowLocationDropdown(false);
    setLocationHighlightedIndex(-1);
    setIsLocationKeyboardNav(false);
  };

  const handleLocationKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setLocationSearchQuery('');
      setLocationSearchResults([]);
      setShowLocationDropdown(false);
      setLocationHighlightedIndex(-1);
      if (!defaultLocationName) {
        setDefaultLocationName(originalValues.defaultLocationName || '');
        setDefaultLocationType(originalValues.defaultLocationType || '');
      }
      return;
    }

    if (!showLocationDropdown || locationSearchResults.length === 0) return;

    const flatResults = getFlattenedLocationResults();
    const maxIndex = flatResults.length - 1;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        setIsLocationKeyboardNav(true);
        setLocationHighlightedIndex(prev => {
          const nextIndex = e.key === 'ArrowDown'
            ? (prev < maxIndex ? prev + 1 : 0)
            : (prev > 0 ? prev - 1 : maxIndex);
          scrollToLocationHighlighted(nextIndex);
          return nextIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (flatResults.length > 0) {
          const index = locationHighlightedIndex === -1 ? 0 : locationHighlightedIndex;
          handleLocationResultSelect(flatResults[index]);
        }
        break;
      default:
        setIsLocationKeyboardNav(false);
        break;
    }
  };

  const getLocationDisplayName = (result) => {
    if ((result.type === 'county' || result.type === 'city') && result.name.includes(', ')) {
      const parts = result.name.split(', ');
      const local = parts.slice(0, -1).join(', ');
      const state = parts[parts.length - 1];
      const stateAbbr = getStateAbbreviation(state.trim());
      return `${local}, ${stateAbbr}`;
    }
    return result.name;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose} onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); document.activeElement?.blur(); } }}>
      <div className="profile-modal" onClick={e => e.stopPropagation()}>
        <button className="close-button" onClick={handleClose}>
          <X size={24} />
        </button>

        {loading ? (
          <div className="profile-loading">
            <Loader2 className="spinner" size={32} />
            <p>Loading Profile...</p>
          </div>
        ) : (
          <div className="profile-modal-content">
            <nav className="profile-nav">
              <div className="profile-nav-header">
                <div className="profile-nav-title">
                  <SquareUserRound size={32} className="profile-nav-icon" />
                  <span className="profile-nav-title-text">Profile</span>
                </div>
              </div>

              <div className="profile-nav-items">
                <button 
                  className={`profile-nav-item ${activeSection === 'account' ? 'active' : ''}`}
                  onClick={() => setActiveSection('account')}
                >
                  <User size={18} />
                  <span>Account</span>
                  <ChevronRight size={16} className="nav-arrow" />
                </button>
                <button 
                  className={`profile-nav-item ${activeSection === 'settings' ? 'active' : ''}`}
                  onClick={() => setActiveSection('settings')}
                >
                  <Settings size={18} />
                  <span>Settings</span>
                  <ChevronRight size={16} className="nav-arrow" />
                </button>
                <button 
                  className={`profile-nav-item ${activeSection === 'subscription' ? 'active' : ''}`}
                  onClick={() => setActiveSection('subscription')}
                >
                  <CreditCard size={18} />
                  <span>Subscription</span>
                  <ChevronRight size={16} className="nav-arrow" />
                </button>
                <button 
                  className={`profile-nav-item ${activeSection === 'history' ? 'active' : ''}`}
                  onClick={() => setActiveSection('history')}
                >
                  <History size={18} />
                  <span>Search History</span>
                  <ChevronRight size={16} className="nav-arrow" />
                </button>
              </div>

              <div className="profile-nav-footer">
                <button className="profile-nav-item sign-out" onClick={handleSignOut}>
                  <span>Log Out</span>
                </button>
              </div>
            </nav>

            <div className="profile-main">

              {activeSection === 'account' && (
                <div className="profile-section">
                  <h2>Account Information</h2>

                  {message.text && (
                    <div className={`profile-message ${message.type}`}>
                      {message.type === 'success' ? <Check size={18} /> : <AlertTriangle size={18} />}
                      {message.text}
                    </div>
                  )}

                  {(() => {
                    const plan = profile?.subscription_plan || 'free';
                    const limits = PLAN_LIMITS[plan] || PLAN_LIMITS.free;
                    const usedSearches = liveSearchesUsed ?? (profile?.monthly_searches_used || 0);
                    const exportsUsed = liveExportsUsed ?? (profile?.monthly_exports_used || 0);
                    const printsUsed = livePrintsUsed ?? (profile?.monthly_prints_used || 0);
                    const alertsUsed = Array.isArray(profile?.alerts) ? profile.alerts.length : 0;
                    const combined = hasCombinedOutputs(plan);

                    const secondaryStat = combined
                      ? {
                          label: 'Outputs',
                          value: limits.outputs === null
                            ? 'Unlimited'
                            : <>{exportsUsed + printsUsed}<span className="account-usage-stat-limit">/{limits.outputs}</span></>
                        }
                      : {
                          label: 'Prints',
                          value: limits.prints === null
                            ? 'Unlimited'
                            : <>{printsUsed}<span className="account-usage-stat-limit">/{limits.prints}</span></>
                        };

                    return (
                      <div className={`account-usage-summary account-usage-summary--${plan}`}>
                        <div className="account-usage-plan">
                          <span className="account-plan-label">{limits.label} Plan</span>
                          <button className="account-plan-upgrade-link" onClick={() => setActiveSection('subscription')}>
                            {plan === 'free' ? 'Upgrade' : 'Manage'}
                          </button>
                        </div>
                        <div className="account-usage-stats">
                          <div className="account-usage-stat">
                            <span className="account-usage-stat-value">
                              {limits.searches === null
                                ? 'Unlimited'
                                : <>{usedSearches}<span className="account-usage-stat-limit">/{limits.searches}</span></>
                              }
                            </span>
                            <span className="account-usage-stat-label">Searches</span>
                          </div>
                          <div className="account-usage-divider" />
                          <div className="account-usage-stat">
                            <span className="account-usage-stat-value">{secondaryStat.value}</span>
                            <span className="account-usage-stat-label">{secondaryStat.label}</span>
                          </div>
                          <div className="account-usage-divider" />
                          <div className="account-usage-stat">
                            <span className="account-usage-stat-value">
                              {limits.alerts === null
                                ? 'Unlimited'
                                : <>{alertsUsed}<span className="account-usage-stat-limit">/{limits.alerts}</span></>
                              }
                            </span>
                            <span className="account-usage-stat-label">Alerts</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="profile-form-content">
                    <div className="profile-form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Enter your name"
                        className="profile-input name"
                      />
                    </div>

                    <div className="profile-form-group">
                      <label>Email Address / Username</label>
                      <input
                        type="text"
                        value={profile?.email || user?.email || ''}
                        disabled
                        className="profile-input disabled"
                        autoComplete="username"
                      />
                    </div>

                    <div className="profile-form-group">
                      <label>Log In Method(s)</label>
                      <div className="profile-login-methods">
                        {(profile?.login_providers?.length
                          ? profile.login_providers
                          : (user?.app_metadata?.providers ||
                              user?.identities?.map((i) => i.provider).filter(Boolean) ||
                              [])
                        ).map((provider) => (
                          <span key={provider} className="login-method-badge">
                            {provider === 'email' && (
                              <>
                                <Mail size={14} />
                                Email
                              </>
                            )}
                            {provider === 'google' && (
                              <>
                                <svg viewBox="0 0 24 24" width="14" height="14">
                                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                                Google
                              </>
                            )}
                            {provider === 'facebook' && (
                              <>
                                <svg viewBox="0 0 24 24" width="14" height="14" fill="#1877f2">
                                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                                Facebook
                              </>
                            )}
                            {(provider === 'x' || provider === 'twitter') && (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                                X (Twitter)
                              </>
                            )}
                            {provider === 'apple' && (
                              <>
                                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                                </svg>
                                Apple
                              </>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="profile-form-group">
                      <label>Member Since</label>
                      <p className="profile-static-value">{formatDate(profile?.created_at)}</p>
                    </div>
                  </div>

                  <div className="profile-actions">
                    <button 
                      className="profile-delete-link"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      Delete my account
                    </button>
                    <button 
                      className="profile-save-btn"
                      onClick={handleSaveProfile}
                      disabled={saving || !hasChanges}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}

              {activeSection === 'settings' && (
                <div className="profile-section">
                  <h2>Default Settings</h2>

                  {message.text && (
                    <div className={`profile-message ${message.type}`}>
                      {message.type === 'success' ? <Check size={18} /> : <AlertTriangle size={18} />}
                      {message.text}
                    </div>
                  )}

                  <div className="profile-form-content">
                    <div className="profile-form-group">
                      <label>
                        <MapPin size={16} />
                        Default Location
                      </label>
                      <div className="profile-location-search-wrapper" ref={locationSearchRef}>
                        <div className="search-container profile-location-search">
                          <input
                            ref={locationInputRef}
                            type="text"
                            className="search-input"
                            placeholder="Enter city, county or state"
                            value={locationSearchQuery || formatLocationDisplay(defaultLocationName, defaultLocationType)}
                            onChange={handleLocationSearch}
                            onKeyDown={handleLocationKeyDown}
                          />
                          {(locationSearchQuery || defaultLocationName) && (
                            <button
                              className="clear-search"
                              style={{ right: '8px' }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setLocationSearchQuery('');
                                setDefaultLocationName('');
                                setDefaultLocationType('');
                                setLocationSearchResults([]);
                                setShowLocationDropdown(false);
                                setLocationHighlightedIndex(-1);
                                locationInputRef.current?.focus();
                              }}
                            >
                              <X size={16} />
                            </button>
                          )}

                          {showLocationDropdown && locationSearchResults.length > 0 && createPortal(
                            <ul
                              className="search-dropdown profile-location-dropdown"
                              ref={locationDropdownRef}
                              data-keyboard-nav={isLocationKeyboardNav}
                              style={{
                                position: 'absolute',
                                top: `${locationInputRef.current.getBoundingClientRect().bottom + window.scrollY + 4}px`,
                                left: `${locationInputRef.current.getBoundingClientRect().left + window.scrollX}px`,
                                width: `${locationInputRef.current.getBoundingClientRect().width}px`,
                                zIndex: 10003
                              }}
                            >
                              {locationTypes.slice().reverse().map(locationType => {
                                const typeResults = locationSearchResults.filter(result => result.type === locationType.value);
                                if (typeResults.length === 0) return null;

                                return (
                                  <div key={locationType.value} className="search-dropdown-section">
                                    <h6 className="search-dropdown-heading">
                                      {locationType.label}
                                    </h6>
                                    {typeResults.map((result) => {
                                      const globalIndex = getFlattenedLocationResults().findIndex(
                                        item => item.name === result.name && item.type === result.type
                                      );
                                      return (
                                        <li
                                          key={result.name}
                                          data-index={globalIndex}
                                          className={globalIndex === locationHighlightedIndex ? 'highlighted' : ''}
                                          onClick={() => handleLocationResultSelect(result)}
                                          onMouseEnter={() => {
                                            setLocationHighlightedIndex(globalIndex);
                                            setIsLocationKeyboardNav(false);
                                          }}
                                        >
                                          {getLocationDisplayName(result)}
                                        </li>
                                      );
                                    })}
                                  </div>
                                );
                              })}
                            </ul>,
                            document.body
                          )}
                        </div>
                      </div>
                      <span className="profile-input-hint">
                        This is the location that will load when you log in
                        {(profile?.subscription_plan || 'free') === 'free'
                          ? ' (changing counts toward your search limit)'
                          : ' (changing counts toward your monthly limit)'}
                      </span>
                    </div>

                    <div className="profile-form-group">
                      <label>
                        <ShieldAlert size={16} />
                        Default Crime Type
                      </label>
                      <div className={`profile-custom-dropdown ${isCrimeDropdownOpen ? 'open' : ''}`}>
                        <button
                          type="button"
                          ref={crimeButtonRef}
                          className="profile-dropdown-trigger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsCrimeDropdownOpen(!isCrimeDropdownOpen);
                            setIsViewDropdownOpen(false);
                            setIsHistoryMonthDropdownOpen(false);
                          }}
                        >
                          {getSelectedCrimeLabel()}
                        </button>
                        {isCrimeDropdownOpen && createPortal(
                          <div 
                            className="profile-dropdown-menu"
                            style={{
                              position: 'absolute',
                              top: `${crimeButtonRef.current.getBoundingClientRect().bottom + window.scrollY + 4}px`,
                              left: `${crimeButtonRef.current.getBoundingClientRect().left + window.scrollX}px`,
                              minWidth: `${crimeButtonRef.current.getBoundingClientRect().width}px`,
                              zIndex: 10002,
                              maxHeight: `${Math.min(
                                window.innerHeight - crimeButtonRef.current.getBoundingClientRect().bottom - 24,
                                Math.floor(window.innerHeight * 0.6)
                              )}px`
                            }}
                          >
                            <div className="profile-dropdown-menu-section">
                              <div 
                                className={`profile-dropdown-item ${defaultCrime === 'all_crime' ? 'selected' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (defaultCrime !== 'all_crime') {
                                    setDefaultCrime('all_crime');
                                    setIsCrimeDropdownOpen(false);
                                  }
                                }}
                              >
                                All Crime
                              </div>
                            
                              {getCrimeTypes('FBI_CIUS').filter(type => type.value !== 'all_crime').map((type, index) => (
                                <div key={type.value}>
                                  <div className="profile-dropdown-submenu">
                                    <div 
                                      className={`profile-dropdown-item ${defaultCrime === type.value ? 'selected' : ''}`}
                                      style={{ 
                                        borderTop: index > 0 ? '1px solid var(--border-color)' : 'none' 
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (defaultCrime !== type.value) {
                                          setDefaultCrime(type.value);
                                          setIsCrimeDropdownOpen(false);
                                        }
                                      }}
                                    >
                                      {type.label}
                                    </div>
                                  
                                    {type.subTypes && (
                                      <div className="profile-dropdown-submenu">
                                        {type.subTypes.map(subType => (
                                          <div 
                                            key={subType.value} 
                                            className={`profile-dropdown-item ${defaultCrime === subType.value ? 'selected' : ''}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (defaultCrime !== subType.value) {
                                                setDefaultCrime(subType.value);
                                                setIsCrimeDropdownOpen(false);
                                              }
                                            }}
                                          >
                                            {subType.label}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>,
                          document.body
                        )}
                      </div>
                    </div>

                    <div className="profile-form-group">
                      <label>
                        <PictureInPicture2 size={16} />
                        Default View
                      </label>
                      <div className={`profile-custom-dropdown ${isViewDropdownOpen ? 'open' : ''}`}>
                        <button
                          type="button"
                          ref={viewButtonRef}
                          className="profile-dropdown-trigger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsViewDropdownOpen(!isViewDropdownOpen);
                            setIsCrimeDropdownOpen(false);
                            setIsHistoryMonthDropdownOpen(false);
                          }}
                        >
                          {getSelectedViewLabel()}
                        </button>
                        {isViewDropdownOpen && createPortal(
                          <div 
                            className="profile-dropdown-menu"
                            style={{
                              position: 'absolute',
                              top: `${viewButtonRef.current.getBoundingClientRect().bottom + window.scrollY + 4}px`,
                              left: `${viewButtonRef.current.getBoundingClientRect().left + window.scrollX}px`,
                              minWidth: `${viewButtonRef.current.getBoundingClientRect().width}px`,
                              zIndex: 10002
                            }}
                          >
                            {viewTypes.map(view => (
                              <div
                                key={view.value}
                                className={`profile-dropdown-item ${defaultView === view.value ? 'selected' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (defaultView !== view.value) {
                                    setDefaultView(view.value);
                                    setIsViewDropdownOpen(false);
                                  }
                                }}
                              >
                                {view.label}
                              </div>
                            ))}
                          </div>,
                          document.body
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="profile-actions">
                    <div></div>
                    <button 
                      className="profile-save-btn"
                      onClick={handleSaveProfile}
                      disabled={saving || !hasChanges || !defaultLocationName}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}

              {activeSection === 'subscription' && (() => {
                const plan = profile?.subscription_plan || 'free';
                const planOrder = ['free', 'lieutenant', 'captain', 'chief'];

                return (
                  <div className="profile-section subscription-section">
                    <h2>Subscription Plan</h2>

                    <div className="subscription-grid">
                      <div className={`subscription-card ${plan === 'free' ? 'current' : ''}`}>
                        <h3>Free</h3>
                        <p className="subscription-price">$0<span>/mo</span></p>
                        <ul className="subscription-features">
                          <li><Check size={14} /> 5 location searches to get started</li>
                          <li><Check size={14} /> Advanced analytics</li>
                          <li><Check size={14} /> Access to all locations</li>
                          <li><Check size={14} /> Most recent year data</li>
                        </ul>
                        {plan === 'free'
                          ? <button className="subscription-btn current" disabled>Current Plan</button>
                          : planOrder.indexOf(plan) > 0 && <button className="subscription-btn downgrade">Downgrade</button>
                        }
                      </div>

                      <div className={`subscription-card ${plan === 'lieutenant' ? 'current' : ''}`}>
                        <h3>Lieutenant</h3>
                        <p className="subscription-price">$9.99<span>/mo</span></p>
                        <ul className="subscription-features">
                          <li>Everything in Free, plus:</li>
                          <li><Check size={14} /> 50 location searches each month</li>
                          <li><Check size={14} /> Historical data access</li>
                          <li><Check size={14} /> Saved searches</li>
                          <li><Check size={14} /> 10 location alerts</li>
                        </ul>
                        {plan === 'lieutenant'
                          ? <button className="subscription-btn current" disabled>Current Plan</button>
                          : planOrder.indexOf(plan) < planOrder.indexOf('lieutenant')
                            ? <button className="subscription-btn">Upgrade</button>
                            : <button className="subscription-btn downgrade">Downgrade</button>
                        }
                      </div>

                      <div className={`subscription-card ${plan === 'captain' ? 'current' : ''}`}>
                        <h3>Captain</h3>
                        <p className="subscription-price">$19.99<span>/mo</span></p>
                        <ul className="subscription-features">
                          <li>Everything in Lieutenant, plus:</li>
                          <li><Check size={14} /> 200 location searches each month</li>
                          <li><Check size={14} /> 20 location alerts</li>
                          <li><Check size={14} /> Export data to Text/CSV/Excel/PDF/PNG</li>
                        </ul>
                        {plan === 'captain'
                          ? <button className="subscription-btn current" disabled>Current Plan</button>
                          : planOrder.indexOf(plan) < planOrder.indexOf('captain')
                            ? <button className="subscription-btn">Upgrade</button>
                            : <button className="subscription-btn downgrade">Downgrade</button>
                        }
                      </div>

                      <div className={`subscription-card ${plan === 'chief' ? 'current' : ''}`}>
                        <h3>Chief</h3>
                        <p className="subscription-price">$39.99<span>/mo</span></p>
                        <ul className="subscription-features">
                          <li>Everything in Captain, plus:</li>
                          <li><Check size={14} /> Unlimited location searches</li>
                          <li><Check size={14} /> Unlimited location alerts</li>
                          <li><Check size={14} /> Monthly Chief Council</li>
                        </ul>
                        {plan === 'chief'
                          ? <button className="subscription-btn current" disabled>Current Plan</button>
                          : <button className="subscription-btn">Upgrade</button>
                        }
                      </div>
                    </div>
                  </div>
                );
              })()}

              {activeSection === 'history' && (() => {
                const plan = profile?.subscription_plan || 'free';
                const hasEverBeenPaid = profile?.has_ever_been_paid === true;
                const hasConsumedTrial = profile?.has_consumed_trial === true;
                const neverPaidFree = plan === 'free' && !hasEverBeenPaid;
                const archive =
                  profile?.search_history_archive &&
                  typeof profile.search_history_archive === 'object' &&
                  !Array.isArray(profile.search_history_archive)
                    ? profile.search_history_archive
                    : {};

                // Current calendar month key in UTC, used to merge mid-month archive
                // entries (created by plan changes) back into the Current Month view.
                const nowForMonthKey = new Date();
                const currentMonthKey = `${nowForMonthKey.getUTCFullYear()}-${String(nowForMonthKey.getUTCMonth() + 1).padStart(2, '0')}`;
                const currentMonthArchive = Array.isArray(archive[currentMonthKey])
                  ? archive[currentMonthKey]
                  : [];

                // The archive dropdown should only show *prior* months. The current month,
                // even if it has archived segments from a mid-month plan change, is shown
                // in the main Current Month view.
                const archiveKeys = Object.keys(archive)
                  .filter((k) => k !== currentMonthKey && Array.isArray(archive[k]) && archive[k].length > 0)
                  .sort()
                  .reverse();

                const fetchedHistory = (() => {
                  if (neverPaidFree || plan === 'free') {
                    return Array.isArray(profile?.search_history) ? profile.search_history : [];
                  }
                  const now = new Date();
                  const resetDate = profile?.search_reset_date ? new Date(profile.search_reset_date) : null;
                  return !isNewUtcMonth(now, resetDate) && Array.isArray(profile?.search_history)
                    ? profile.search_history
                    : [];
                })();

                // Current Month view merges: current search_history + any archived segments
                // from this same calendar month (created by mid-month plan changes).
                const liveOrFetched =
                  liveSearchHistory && liveSearchHistory.length > 0
                    ? liveSearchHistory
                    : fetchedHistory;
                const currentMonthMerged = [...currentMonthArchive, ...liveOrFetched];
                const noSearchesThisMonth = currentMonthMerged.length === 0;

                const merged =
                  historyMonthKey !== 'current' && Array.isArray(archive[historyMonthKey])
                    ? archive[historyMonthKey]
                    : currentMonthMerged;

                const filterQ = historyLocationFilter.trim().toLowerCase();
                const matchesLocationFilter = (e) =>
                  !filterQ ||
                  String(e.locationName || '')
                    .toLowerCase()
                    .includes(filterQ);

                // Sort ascending for numbering purposes (oldest = 1, newest = N).
                // Number from the full month list first so a location filter does not
                // renumber rows (e.g. search #64 stays #64 when filtered).
                const sortedAsc = [...merged].sort(
                  (a, b) => new Date(a.searchedAt) - new Date(b.searchedAt)
                );
                const sorted = sortedAsc.filter(matchesLocationFilter);

                const tz = getDetectedTimeZone();
                const formatAt = (iso) =>
                  new Date(iso).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZone: tz
                  });

                // Build segments (groups by plan) from the full ascending-sorted list.
                // Number within each segment: 1 = oldest in this segment, N = newest.
                // Then when we reverse for display, numbers descend visually (N at top, 1 at bottom).
                const ascSegments = [];
                {
                  let cur = [];
                  let curPlan = null;
                  for (const entry of sortedAsc) {
                    const p = String(entry.plan_at_search || 'free').toLowerCase();
                    if (!cur.length) {
                      curPlan = p;
                      cur.push(entry);
                    } else if (p === curPlan) {
                      cur.push(entry);
                    } else {
                      ascSegments.push({ plan: curPlan, items: cur });
                      cur = [entry];
                      curPlan = p;
                    }
                  }
                  if (cur.length) ascSegments.push({ plan: curPlan, items: cur });
                }

                // Reverse segment order for display (newest plan at top).
                // Within each segment, reverse items so newest is first.
                // Numbering is per-segment on the unfiltered list; filter afterward.
                const displaySegments = [...ascSegments]
                  .reverse()
                  .map((seg) => {
                    const itemsDesc = [...seg.items].reverse();
                    const segmentLen = itemsDesc.length;
                    return {
                      plan: seg.plan,
                      count: segmentLen,
                      items: itemsDesc
                        .map((entry, idx) => ({
                          entry,
                          // idx === 0 is the newest in this segment — gets the highest number.
                          displayNum: segmentLen - idx
                        }))
                        .filter(({ entry }) => matchesLocationFilter(entry)),
                      // The "upgrade timestamp" for a segment header is the time of the
                      // first (oldest) search in that plan's segment (ascending original).
                      upgradedAt: seg.items[0].searchedAt
                    };
                  })
                  .filter((seg) => seg.items.length > 0);

                const listElements = [];
                const multiPlan = displaySegments.length > 1;
                displaySegments.forEach((seg, si) => {
                  const segmentKey = `${seg.plan}-${seg.items[0]?.entry.searchedAt}-${seg.count}`;
                  const isCollapsed = collapsedPlanSegments.has(segmentKey);
                  if (multiPlan) {
                    // si === last index means this is the oldest plan (bottom) — no "upgraded" for it
                    const isOldestSegment = si === displaySegments.length - 1;
                    listElements.push({
                      key: `h-${si}-${seg.items[0]?.entry.searchedAt}`,
                      kind: 'header',
                      plan: seg.plan,
                      count: seg.count,
                      upgradedAt: isOldestSegment ? null : seg.upgradedAt,
                      segmentKey,
                      isCollapsed
                    });
                  }
                  if (!isCollapsed) {
                    seg.items.forEach(({ entry, displayNum }, j) => {
                      listElements.push({
                        key: `r-${si}-${j}-${entry.searchedAt}`,
                        kind: 'row',
                        entry,
                        displayNum
                      });
                    });
                  }
                });

                const isChief = plan === 'chief';
                const isPastMonth = historyMonthKey !== 'current' && Array.isArray(archive[historyMonthKey]);
                const hasAnyUnfilteredResults = merged.length > 0;
                const filterActive = filterQ.length > 0;
                const emptyMessage = filterActive && hasAnyUnfilteredResults
                  ? `No searches match "${historyLocationFilter.trim()}"`
                  : isPastMonth
                    ? 'No searches recorded for this month.'
                    : neverPaidFree
                      ? 'No searches yet.'
                      : 'No searches yet this month';

                const showLocationsHeader = merged.length > 0;
                const hasArchiveHistory = archiveKeys.length > 0;
                const hasCurrentMonthHistory = currentMonthMerged.length > 0;
                const showHistoryMonthDropdown =
                  !neverPaidFree && (hasCurrentMonthHistory || hasArchiveHistory);
                const centerHistoryMonthDropdown =
                  showHistoryMonthDropdown && noSearchesThisMonth && sorted.length === 0;

                // Banner only helps when the user actually has nothing meaningful
                // to show. If they have 5+ searches across current + archived history,
                // the history itself explains where their data is (via month dropdown).
                const totalSearchesAllTime = (() => {
                  const current = Array.isArray(profile?.search_history)
                    ? profile.search_history.length
                    : 0;
                  const archiveTotal = Object.values(archive).reduce(
                    (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
                    0
                  );
                  return current + archiveTotal;
                })();
                const showReturningUserBanner =
                  plan === 'free' && hasConsumedTrial && totalSearchesAllTime < 5;

                return (
                  <div className="profile-section">
                    <h2>Search History</h2>

                    {(showLocationsHeader || showHistoryMonthDropdown) && (
                      <>
                        <div
                          className={`history-section-heading-row ${
                            centerHistoryMonthDropdown ? 'history-section-heading-row--centered' : ''
                          }`}
                        >
                          {showLocationsHeader && (
                            <h3 className="history-section-heading">
                              <MapPin size={16} />
                              <span>Location{sorted.length !== 1 ? 's' : ''}{!isChief && !isPastMonth && sorted.length > 0 ? '*' : ''}</span>
                            </h3>
                          )}
                        </div>
                        <div className="history-controls-row">
                          {showLocationsHeader && (
                            <div className="history-filter-wrapper">
                              <input
                                id="history-location-filter"
                                type="search"
                                className="history-filter-input"
                                placeholder="Filter by location"
                                value={historyLocationFilter}
                                onChange={(e) => setHistoryLocationFilter(e.target.value)}
                                autoComplete="off"
                              />
                              {historyLocationFilter && (
                                <button
                                  type="button"
                                  className="history-filter-clear"
                                  onClick={() => setHistoryLocationFilter('')}
                                  aria-label="Clear filter"
                                >
                                  <X size={16} />
                                </button>
                              )}
                            </div>
                          )}
                          {showHistoryMonthDropdown && (
                          <div className={`profile-custom-dropdown history-month-dropdown ${isHistoryMonthDropdownOpen ? 'open' : ''}`}>
                            <button
                              type="button"
                              ref={historyMonthButtonRef}
                              className="profile-dropdown-trigger history-month-dropdown-trigger"
                              aria-haspopup="listbox"
                              aria-expanded={isHistoryMonthDropdownOpen}
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsHistoryMonthDropdownOpen(!isHistoryMonthDropdownOpen);
                                setIsCrimeDropdownOpen(false);
                                setIsViewDropdownOpen(false);
                              }}
                            >
                              <span className="history-month-dropdown-stack">
                                <span className="history-month-dropdown-label">
                                  {historyMonthKey === 'current'
                                    ? 'Current Month'
                                    : formatHistoryMonthLabel(historyMonthKey)}
                                </span>
                                <span className="history-month-dropdown-sizer" aria-hidden="true">
                                  Current Month
                                </span>
                                {archiveKeys.map((key) => (
                                  <span
                                    key={key}
                                    className="history-month-dropdown-sizer"
                                    aria-hidden="true"
                                  >
                                    {formatHistoryMonthLabel(key)}
                                  </span>
                                ))}
                              </span>
                            </button>
                            {isHistoryMonthDropdownOpen && historyMonthButtonRef.current && createPortal(
                              <div
                                className="profile-dropdown-menu history-month-dropdown-menu"
                                role="listbox"
                                style={{
                                  position: 'absolute',
                                  top: `${historyMonthButtonRef.current.getBoundingClientRect().bottom + window.scrollY + 4}px`,
                                  left: `${historyMonthButtonRef.current.getBoundingClientRect().right + window.scrollX}px`,
                                  transform: 'translateX(-100%)',
                                  width: `${historyMonthButtonRef.current.getBoundingClientRect().width}px`,
                                  minWidth: `${historyMonthButtonRef.current.getBoundingClientRect().width}px`,
                                  boxSizing: 'border-box',
                                  zIndex: 10002,
                                  maxHeight: `${Math.min(
                                    window.innerHeight - historyMonthButtonRef.current.getBoundingClientRect().bottom - 24,
                                    Math.floor(window.innerHeight * 0.6)
                                  )}px`
                                }}
                              >
                                <div
                                  className={`profile-dropdown-item ${historyMonthKey === 'current' ? 'selected' : ''}`}
                                  role="option"
                                  aria-selected={historyMonthKey === 'current'}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (historyMonthKey !== 'current') {
                                      setHistoryMonthKey('current');
                                      setIsHistoryMonthDropdownOpen(false);
                                    }
                                  }}
                                >
                                  Current Month
                                </div>
                                {archiveKeys.map((key) => (
                                  <div
                                    key={key}
                                    className={`profile-dropdown-item ${historyMonthKey === key ? 'selected' : ''}`}
                                    role="option"
                                    aria-selected={historyMonthKey === key}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (historyMonthKey !== key) {
                                        setHistoryMonthKey(key);
                                        setIsHistoryMonthDropdownOpen(false);
                                      }
                                    }}
                                  >
                                    {formatHistoryMonthLabel(key)}
                                  </div>
                                ))}
                              </div>,
                              document.body
                            )}
                          </div>
                          )}
                        </div>
                      </>
                    )}

                    {sorted.length === 0 ? (
                      <div className="history-empty">
                        <Clock size={32} className="history-empty-icon" />
                        <p>{emptyMessage}</p>
                        {showReturningUserBanner && (
                          <div className="history-empty-banner">
                            <p>
                              {hasEverBeenPaid ? (
                                <>
                                  <strong>You&apos;re now on the Free plan.</strong> The 5 free
                                  searches are available only before your first paid subscription.
                                  Since you&apos;ve had a paid plan, these free searches are no
                                  longer active. You can still view your default location, but new
                                  searches require a paid plan.
                                </>
                              ) : (
                                <>
                                  <strong>Your free trial has already been used.</strong> Free
                                  searches are available once per account. Because this account
                                  previously used the free trial, new searches require a paid plan.
                                  You can still view your default location.
                                </>
                              )}
                            </p>
                            <button
                              type="button"
                              className="profile-former-subscriber-upgrade"
                              onClick={() => setActiveSection('subscription')}
                            >
                              View Paid Plans
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <ul className="history-list">
                        {listElements.map((el) =>
                          el.kind === 'header' ? (
                            <li
                              key={el.key}
                              className="history-plan-separator"
                              onClick={() => {
                                setCollapsedPlanSegments((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(el.segmentKey)) next.delete(el.segmentKey);
                                  else next.add(el.segmentKey);
                                  return next;
                                });
                              }}
                            >
                              <span className="history-plan-separator-left">
                                <ChevronDown
                                  size={16}
                                  className={`history-plan-separator-chevron ${
                                    el.isCollapsed ? 'collapsed' : ''
                                  }`}
                                />
                                <span className="history-plan-separator-plan">
                                  {planSlugToHeaderLabel(el.plan)} PLAN
                                </span>
                                <span className="history-plan-separator-count">
                                  ({el.count} search{el.count === 1 ? '' : 'es'} used)
                                </span>
                              </span>
                              {el.upgradedAt && (
                                <span className="history-plan-separator-upgraded">
                                  Upgraded {formatAt(el.upgradedAt)}
                                </span>
                              )}
                            </li>
                          ) : (
                            <li key={el.key} className="history-item">
                              <span className="history-item-number">{el.displayNum}</span>
                              <div className="history-item-info">
                                <span className="history-item-name">
                                  {formatLocationNameWithStateAbbrev(
                                    el.entry.locationName,
                                    el.entry.locationType
                                  )}
                                </span>
                                <span className="history-item-type">{el.entry.locationType}</span>
                              </div>
                              <span className="history-item-time">{formatAt(el.entry.searchedAt)}</span>
                            </li>
                          )
                        )}
                      </ul>
                    )}

                    {!isChief && sorted.length > 0 && !isPastMonth && (
                      <p className="history-footnote">
                        * Revisiting a previously searched location, or opening your default
                        location, does not count toward your{' '}
                        {plan === 'free' ? 'search' : 'monthly'} limit.
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="profile-mobile-footer">
              <button className="profile-mobile-footer-btn" onClick={handleSignOut}>
                Log Out
              </button>
            </div>
          </div>
        )}

        {showDeleteConfirm && (
          <div className="delete-confirm-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="delete-confirm-modal" onClick={e => e.stopPropagation()}>
              <AlertTriangle size={48} className="delete-warning-icon" />
              <h3>Delete Your Account?</h3>
              <p>This action cannot be undone. All your account data, saved locations, and settings will be permanently deleted.</p>
              <p className="delete-confirm-instruction">Type <strong>DELETE</strong> to confirm:</p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                className="delete-confirm-input"
              />
              <div className="delete-confirm-actions">
                <button 
                  className="delete-cancel-btn"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="delete-confirm-btn"
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== 'DELETE'}
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileModal;